/**
 * Root route. Renders the application shell when the user is signed in
 * and the Clerk-managed passkey sign-in surface when they are not. The
 * outer Cloudflare Access layer is enforced upstream; this gate
 * defends against a misconfiguration where the edge is bypassed.
 *
 * The shell layout: 240px sidebar plus a scrolling main content
 * region. Global mount points for the Cmd-K command bar and the
 * task/deal slide-over live here so any nested view can open them
 * through the shared Zustand stores without prop-drilling.
 *
 * Reference: spec sections 7.1, 7.2, 9.1, and Claude Design Files
 * README sections 5 and 6.
 */

import { Outlet, createRootRoute } from '@tanstack/react-router';
import { SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';

import { Sidebar } from '@/components/shared/Sidebar';
import { CommandBar } from '@/components/shared/CommandBar';
import { SlideOver } from '@/components/shared/SlideOver';
import { CheatSheet } from '@/components/shared/CheatSheet';
import { ServiceWorkerUpdate } from '@/components/shared/ServiceWorkerUpdate';
import { CelebrationMoment } from '@/components/sales/CelebrationMoment';
import { useCommandBarShortcut } from '@/lib/use-command-bar-shortcut';
import { useKeyboardShortcuts } from '@/lib/keyboard-shortcuts';
import { useAuthMe } from '@/lib/api/auth';

export const Route = createRootRoute({
  component: RootLayout,
});

/**
 * Build-time bypass for the Clerk auth gate. Set to `'true'` only on
 * the Playwright E2E build (CI workflow + playwright.config.ts
 * webServer) so the smoke tests can drive the shell without standing
 * up a real Clerk session. Always false in production builds.
 */
const E2E_AUTH_BYPASS = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

function RootLayout() {
  // Keyboard hooks mount at the root so bindings survive route changes.
  useCommandBarShortcut();
  useKeyboardShortcuts();

  if (E2E_AUTH_BYPASS) {
    // E2E bypass skips both Clerk and the /auth/me first-login upsert.
    // Playwright runs without a real backend session — the smoke specs
    // only assert that the shell renders and navigation works.
    return <Shell skipAuthGate />;
  }

  return (
    <>
      <SignedIn>
        <Shell />
      </SignedIn>
      <SignedOut>
        <div className="flex min-h-screen items-center justify-center bg-mx-near-black p-mx-5">
          {/* Clerk's hosted sign-in component carries the passkey flow.
           * The dark appearance object tunes the surfaces to the
           * MAXART palette without straying from the locked tokens. */}
          <SignIn
            appearance={{
              elements: {
                rootBox: 'w-full max-w-[400px]',
                card: 'bg-mx-grey-950 border border-mx-grey-800 shadow-none',
                headerTitle: 'text-mx-white font-display',
                headerSubtitle: 'text-mx-grey-400',
                socialButtonsBlockButton: 'border-mx-grey-800',
              },
            }}
          />
        </div>
      </SignedOut>
    </>
  );
}

function Shell({ skipAuthGate = false }: { skipAuthGate?: boolean }) {
  // First-login upsert per spec section 9.1. Must resolve before any
  // other authenticated query fires, otherwise `requireUserId` 401s
  // every downstream endpoint with "No local user record."
  //
  // The E2E bypass path skips this — Playwright runs without a real
  // Clerk session so the query stays `isLoading: true` forever (it is
  // gated on `isSignedIn === true`). Without the skip, the smoke
  // specs would render the "SIGNING YOU IN…" splash and never see
  // the shell.
  return skipAuthGate ? <ShellBody /> : <ShellWithAuthGate />;
}

function ShellWithAuthGate() {
  const auth = useAuthMe();

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mx-near-black text-mx-grey-400">
        <p className="mono-label">SIGNING YOU IN…</p>
      </div>
    );
  }
  if (auth.isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-mx-3 bg-mx-near-black p-mx-5 text-mx-grey-300">
        <p className="mono-label text-mx-neg">SIGN-IN FAILED</p>
        <p className="max-w-[480px] text-mx-body-sm">{auth.error.message}</p>
        <p className="text-mx-body-sm text-mx-grey-mid">
          Refresh to retry. If this persists, the Clerk email on this account is probably not in the
          backend ALLOWED_EMAILS list.
        </p>
      </div>
    );
  }

  return <ShellBody />;
}

function ShellBody() {
  return (
    <>
      <div className="flex min-h-screen bg-mx-near-black text-mx-white">
        <Sidebar />
        <main className="flex-1 overflow-y-auto px-mx-6">
          <Outlet />
        </main>
      </div>
      {/* Global mount points. The dialogs are invisible until their
       * respective Zustand store flips its open flag — keeping them
       * mounted at the shell level means any view can call open()
       * without needing to thread props through the route tree.
       *
       * `CelebrationMoment` reads the `useCelebration` store; the
       * board drag and the slide-over "Mark won" / "Mark lost"
       * buttons both call `show()` so the overlay fires regardless
       * of which surface raised the outcome (UAT 7.7-7.9). */}
      <CommandBar />
      <SlideOver />
      <CheatSheet />
      <CelebrationMoment />
      {/* PWA reload toast. Renders nothing while idle. When the service
       * worker detects a new bundle, a small bottom-of-shell banner
       * surfaces a manual "Reload" trigger so the swap happens at the
       * moment Peter chooses rather than mid-task (UAT 50). */}
      <ServiceWorkerUpdate />
    </>
  );
}
